import { ChangeEvent, useState, useRef, useEffect } from 'react'
import { Label, Textarea, Button, Alert, AlertDescription } from '@creanizer/ui'
import { Upload, Info, FileText, X } from 'lucide-react'
import type { WorldBuilderInput } from '@creanizer/types'
import { readMultipleFiles, FILE_ACCEPT, formatFileSize } from '../../../utils/fileReader'

interface IdeaModeFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
}

interface UploadedFile {
  id: string
  name: string
  content: string
  size: number
}

export function IdeaModeForm({ input, onChange }: IdeaModeFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [userText, setUserText] = useState<string>(input.ideaText || '')
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  
  // 初期化時にuserTextを設定
  useEffect(() => {
    const textWithoutFiles = input.ideaText?.split('【')[0].trim() || ''
    setUserText(textWithoutFiles)
  }, []) // 初回のみ実行
  
  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    
    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)
      
      // エラーがある場合は表示
      if (errors.length > 0) {
        alert(`以下のファイルの読み込みに失敗しました:\n${errors.join('\n')}`)
      }
      
      // 成功したファイルを追加
      const newFiles: UploadedFile[] = fileInfos
        .filter(info => !info.error && info.text)
        .map(info => ({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: info.fileName,
          content: info.text,
          size: info.text.length
        }))
      
      setUploadedFiles(prev => [...prev, ...newFiles])
      
      // ideaTextを更新
      updateIdeaText(userText, [...uploadedFiles, ...newFiles])
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      alert('ファイルの読み込みに失敗しました')
    }
    
    // ファイル選択をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  
  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)
    updateIdeaText(userText, newFiles)
  }
  
  const updateIdeaText = (baseText: string, files: UploadedFile[]) => {
    let combinedText = baseText
    
    // アップロードされたファイルの内容を追加
    if (files.length > 0) {
      const filesContent = files.map(file => {
        return `【ファイル: ${file.name}】\n${file.content}`
      }).join('\n\n')
      combinedText = baseText + (baseText ? '\n\n' : '') + filesContent
    }
    
    onChange({
      ...input,
      mode: 'idea',
      ideaText: combinedText,
      ideaFile: files.length > 0 ? combinedText : undefined
    })
  }
  
  const handleUserTextChange = (text: string) => {
    setUserText(text)
    updateIdeaText(text, uploadedFiles)
  }
  
  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          アイディアモードでは、入力されたテキストやファイルの内容から世界観を構築します。
          世界観パラメータ（魔法レベル、科学技術レベルなど）は内容に基づいてAIが自動的に決定します。
          複数のファイルをアップロードすることも可能です（対応形式: .txt, .md, .docx, .pdf）。
        </AlertDescription>
      </Alert>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="ideaText">アイディアを入力</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            ファイルから読み込み
          </Button>
        </div>
        <Textarea
          id="ideaText"
          value={userText}
          onChange={(e) => handleUserTextChange(e.target.value)}
          placeholder="世界観のアイディアを自由に入力してください。断片的な情報でも構いません。"
          rows={8}
        />
        
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_ACCEPT}
          multiple
          onChange={handleFileUpload}
          className="hidden"
        />
        
        {/* 読み込んだファイルのリスト */}
        {uploadedFiles.length > 0 && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
            <div className="text-sm font-medium text-gray-700 mb-2">読み込み済みファイル：</div>
            <div className="space-y-2">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{file.name}</span>
                    <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleFileRemove(file.id)}
                    className="p-1"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
          <FileText className="h-4 w-4" />
          <span>
            {userText.length > 0 ? `${userText.length}文字入力済み` : 'アイディアを入力するか、ファイルをアップロードしてください'}
            {uploadedFiles.length > 0 && ` + ファイル${uploadedFiles.length}個（合計${formatFileSize(uploadedFiles.reduce((sum, f) => sum + f.size, 0))}）`}
          </span>
        </div>
      </div>
    </div>
  )
}